import React, { useState } from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Volume2, VolumeX, Moon, Sun, Palette, Eye, EyeOff, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useDiscrete } from '../contexts/DiscreteContext';
import { useAuth } from '../contexts/SupabaseAuthContext';

export default function Settings() {
  const navigate = useNavigate();
  const { t, language, selectLanguage } = useLanguage();
  const { isMuted, toggleMute } = useSound();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isDiscreteMode, toggleDiscreteMode } = useDiscrete();
  const { updatePassword, isMasterAccess } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLanguageChange = (lang) => {
    selectLanguage(lang);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError(
        language === 'es'
          ? 'La contraseña debe tener al menos 6 caracteres'
          : 'Password must be at least 6 characters'
      );
      return;
    }

    setChangingPassword(true);
    try {
      await updatePassword(newPassword);
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.message || (language === 'es' ? 'No se pudo cambiar la contraseña' : 'Could not change password'));
    }
    setChangingPassword(false);
  };

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      <div style={{
        ...styles.header,
        ...(isDarkMode ? styles.headerDark : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? styles.backButtonDark : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <h2 style={styles.title}>{t('settings')}</h2>
        <div style={styles.spacer} />
      </div>

      <div style={styles.content}>
        {/* Visual Section */}
        <div style={{
          ...styles.card,
          ...(isDarkMode ? styles.cardDark : {}),
        }}>
          <div style={styles.sectionHeader}>
            <Palette size={24} color={isDarkMode ? "#FFD700" : "#FF1493"} />
            <h3 style={{
              ...styles.sectionTitle,
              ...(isDarkMode ? styles.textDark : {}),
            }}>{t('visual')}</h3>
          </div>
          
          <p style={{
            ...styles.sectionDescription,
            ...(isDarkMode ? styles.textMutedDark : {}),
          }}>{t('visualDescription')}</p>
          
          {/* Dark Mode Toggle */}
          <div style={styles.settingRow}>
            <div style={styles.settingLabel}>
              {isDarkMode ? <Moon size={20} color="#FFD700" /> : <Sun size={20} color="#FF1493" />}
              <span style={{
                ...styles.settingLabelText,
                ...(isDarkMode ? styles.textDark : {}),
              }}>
                {t('darkMode')}
              </span>
            </div>
            <button
              onClick={toggleDarkMode}
              style={{
                ...styles.toggleButton,
                ...(isDarkMode ? styles.toggleButtonDarkActive : styles.toggleButtonOff),
              }}
            >
              <div style={{
                ...styles.toggleKnob,
                ...(isDarkMode ? styles.toggleKnobOn : styles.toggleKnobOff),
              }} />
            </button>
          </div>

          {/* Discrete Mode Toggle */}
          <div style={styles.settingRow}>
            <div style={styles.settingLabel}>
              {isDiscreteMode ? <EyeOff size={20} color={isDarkMode ? "#FFD700" : "#FF1493"} /> : <Eye size={20} color={isDarkMode ? "#FFD700" : "#FF1493"} />}
              <div style={styles.settingLabelContent}>
                <span style={{
                  ...styles.settingLabelText,
                  ...(isDarkMode ? styles.textDark : {}),
                }}>
                  {t('discreteMode') || 'Discrete Mode'}
                </span>
                <span style={{
                  ...styles.settingHint,
                  ...(isDarkMode ? styles.textMutedDark : {}),
                }}>
                  {t('discreteModeHint') || 'Blur comic covers'}
                </span>
              </div>
            </div>
            <button
              onClick={toggleDiscreteMode}
              style={{
                ...styles.toggleButton,
                ...(isDiscreteMode ? (isDarkMode ? styles.toggleButtonDarkActive : styles.toggleButtonOn) : styles.toggleButtonOff),
              }}
            >
              <div style={{
                ...styles.toggleKnob,
                ...(isDiscreteMode ? styles.toggleKnobOn : styles.toggleKnobOff),
              }} />
            </button>
          </div>
        </div>

        {/* Language Section */}
        <div style={{
          ...styles.card,
          ...(isDarkMode ? styles.cardDark : {}),
        }}>
          <div style={styles.sectionHeader}>
            <Globe size={24} color={isDarkMode ? "#FFD700" : "#FF1493"} />
            <h3 style={{
              ...styles.sectionTitle,
              ...(isDarkMode ? styles.textDark : {}),
            }}>{t('language')}</h3>
          </div>
          
          <p style={{
            ...styles.sectionDescription,
            ...(isDarkMode ? styles.textMutedDark : {}),
          }}>{t('languageDescription')}</p>
          
          <div style={styles.languageButtons}>
            <button
              onClick={() => handleLanguageChange('en')}
              style={{
                ...styles.languageButton,
                ...(isDarkMode ? styles.languageButtonDark : {}),
                ...(language === 'en' ? (isDarkMode ? styles.languageButtonActiveDark : styles.languageButtonActive) : {}),
              }}
            >
              <span style={styles.flag}>🇺🇸</span>
              <span style={{
                ...styles.languageText,
                ...(isDarkMode ? styles.textDark : {}),
              }}>English</span>
              {language === 'en' && <span style={{
                ...styles.checkmark,
                ...(isDarkMode ? styles.checkmarkDark : {}),
              }}>✓</span>}
            </button>
            
            <button
              onClick={() => handleLanguageChange('es')}
              style={{
                ...styles.languageButton,
                ...(isDarkMode ? styles.languageButtonDark : {}),
                ...(language === 'es' ? (isDarkMode ? styles.languageButtonActiveDark : styles.languageButtonActive) : {}),
              }}
            >
              <span style={styles.flag}>🇪🇸</span>
              <span style={{
                ...styles.languageText,
                ...(isDarkMode ? styles.textDark : {}),
              }}>Español</span>
              {language === 'es' && <span style={{
                ...styles.checkmark,
                ...(isDarkMode ? styles.checkmarkDark : {}),
              }}>✓</span>}
            </button>
          </div>
        </div>

        {/* Sound Section */}
        <div style={{
          ...styles.card,
          ...(isDarkMode ? styles.cardDark : {}),
        }}>
          <div style={styles.sectionHeader}>
            {isMuted ? 
              <VolumeX size={24} color={isDarkMode ? "#FFD700" : "#FF1493"} /> : 
              <Volume2 size={24} color={isDarkMode ? "#FFD700" : "#FF1493"} />
            }
            <h3 style={{
              ...styles.sectionTitle,
              ...(isDarkMode ? styles.textDark : {}),
            }}>{t('sound')}</h3>
          </div>
          
          <p style={{
            ...styles.sectionDescription,
            ...(isDarkMode ? styles.textMutedDark : {}),
          }}>{t('soundDescription')}</p>
          
          <div style={styles.settingRow}>
            <span style={{
              ...styles.settingLabelText,
              ...(isDarkMode ? styles.textDark : {}),
            }}>
              {isMuted ? t('soundOff') : t('soundOn')}
            </span>
            <button
              onClick={toggleMute}
              style={{
                ...styles.toggleButton,
                ...(isMuted ? styles.toggleButtonOff : (isDarkMode ? styles.toggleButtonDarkActive : styles.toggleButtonOn)),
              }}
            >
              <div style={{
                ...styles.toggleKnob,
                ...(isMuted ? styles.toggleKnobOff : styles.toggleKnobOn),
              }} />
            </button>
          </div>
        </div>

        {/* Account Section — hidden for the shared Master account: only the
            owner rotates that password (via Supabase), not individual members. */}
        {!isMasterAccess && (
          <div style={{
            ...styles.card,
            ...(isDarkMode ? styles.cardDark : {}),
          }}>
            <div style={styles.sectionHeader}>
              <Lock size={24} color={isDarkMode ? "#FFD700" : "#FF1493"} />
              <h3 style={{
                ...styles.sectionTitle,
                ...(isDarkMode ? styles.textDark : {}),
              }}>{language === 'es' ? 'Cuenta' : 'Account'}</h3>
            </div>

            <p style={{
              ...styles.sectionDescription,
              ...(isDarkMode ? styles.textMutedDark : {}),
            }}>{language === 'es' ? 'Cambiá tu contraseña.' : 'Change your password.'}</p>

            <form onSubmit={handleChangePassword} style={styles.passwordForm}>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={language === 'es' ? 'Contraseña nueva' : 'New password'}
                style={styles.passwordInput}
                required
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                style={styles.passwordInput}
                required
              />

              {passwordError && <div style={styles.passwordErrorText}>{passwordError}</div>}
              {passwordSuccess && (
                <div style={styles.passwordSuccessText}>
                  {language === 'es' ? '¡Contraseña actualizada!' : 'Password updated!'}
                </div>
              )}

              <button type="submit" style={styles.passwordSubmitButton} disabled={changingPassword}>
                {changingPassword
                  ? '...'
                  : language === 'es' ? 'Cambiar contraseña' : 'Change password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    display: 'flex',
    flexDirection: 'column',
  },
  containerDark: {
    backgroundImage: 'none',
    backgroundColor: '#000000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.3)',
    ...fpHeader,
  },
  headerDark: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #333',
    boxShadow: 'none',
  },
  backButton: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.4)',
    ...fpBackButton,
  },
  backButtonDark: {
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.2)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    color: '#fff',
    ...fpHeaderTitle,
  },
  spacer: {
    width: '44px',
  },
  content: {
    flex: 1,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '500px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    paddingBottom: '40px',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(255, 20, 147, 0.3)',
    ...fpCard,
  },
  cardDark: {
    backgroundColor: '#111111',
    border: '1px solid #333',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  textDark: {
    color: '#ffffff',
  },
  textMutedDark: {
    color: '#aaaaaa',
  },
  sectionDescription: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
  },
  settingLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  settingLabelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  settingLabelText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  settingLabelTextSmall: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
  },
  settingHint: {
    fontSize: '12px',
    color: '#888',
  },
  languageButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  languageButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f8f8f8',
    border: '2px solid #ddd',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  languageButtonDark: {
    backgroundColor: '#1a1a1a',
    border: '2px solid #444',
  },
  languageButtonActive: {
    backgroundColor: 'rgba(255, 20, 147, 0.1)',
    borderColor: '#FF1493',
  },
  languageButtonActiveDark: {
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
    borderColor: '#FFD700',
  },
  flag: {
    fontSize: '28px',
  },
  languageText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  checkmark: {
    fontSize: '20px',
    color: '#FF1493',
    fontWeight: 'bold',
  },
  checkmarkDark: {
    color: '#FFD700',
  },
  toggleButton: {
    width: '60px',
    height: '32px',
    borderRadius: '16px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.3s ease',
    padding: 0,
  },
  toggleButtonSmall: {
    width: '50px',
    height: '26px',
    borderRadius: '13px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.3s ease',
    padding: 0,
  },
  toggleButtonOn: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
  },
  toggleButtonDarkActive: {
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  },
  toggleButtonOff: {
    backgroundColor: '#ccc',
  },
  toggleKnob: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    backgroundColor: 'white',
    position: 'absolute',
    top: '3px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  toggleKnobSmall: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: 'white',
    position: 'absolute',
    top: '3px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  toggleKnobOn: {
    left: '31px',
  },
  toggleKnobOff: {
    left: '3px',
  },
  toggleKnobOnSmall: {
    left: '27px',
  },
  toggleKnobOffSmall: {
    left: '3px',
  },
  divider: {
    height: '1px',
    backgroundColor: '#eee',
    margin: '16px 0',
  },
  dividerDark: {
    backgroundColor: '#333',
  },
  subSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '12px',
  },
  inactivityRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingTop: '8px',
  },
  inactivityLabel: {
    fontSize: '13px',
    color: '#666',
  },
  minutesSelector: {
    display: 'flex',
    gap: '8px',
  },
  minuteButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#f0f0f0',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#333',
  },
  minuteButtonDark: {
    backgroundColor: '#1a1a1a',
    border: '2px solid #444',
    color: '#aaa',
  },
  minuteButtonActive: {
    backgroundColor: 'rgba(255, 20, 147, 0.15)',
    borderColor: '#FF1493',
    color: '#FF1493',
  },
  minuteButtonActiveDark: {
    backgroundColor: 'rgba(255, 215, 0, 0.15)',
    borderColor: '#FFD700',
    color: '#FFD700',
  },
  passwordForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  passwordInput: {
    padding: '14px',
    border: '2px solid #FF1493',
    borderRadius: '10px',
    fontSize: '15px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  passwordSubmitButton: {
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  passwordErrorText: {
    color: '#FF1493',
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: '600',
    padding: '8px',
    backgroundColor: '#FFE8F0',
    borderRadius: '8px',
    border: '1px solid #FF1493',
  },
  passwordSuccessText: {
    color: '#28a745',
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: '600',
    padding: '8px',
    backgroundColor: '#e6f7ea',
    borderRadius: '8px',
    border: '1px solid #28a745',
  },
};
